/**
 * Determines if a given sales team's price tier sequence differs from the primary sales team's sequence.
 */
export const checkIsCustomPrice = (
  sequence: number | string | null | undefined,
  primarySequence: number | string | null | undefined
): boolean => {
  if (sequence == null || primarySequence == null) return true;
  return Number(sequence) !== Number(primarySequence);
};

type AlbumPricingSource = {
  albumSalesTeams?: Array<{
    isPrimary?: boolean | null;
    price?: number | string | null;
    salesTeamName?: string | null;
    currencyName?: string | null;
  }> | null;
  albumMetaData?: {
    pricing?: {
      priceCode?: string | null;
    } | null;
  } | null;
};

const currencySymbols: Record<string, string> = {
  USD: '$',
  AUD: '$',
  EUR: '\u20ac',
  GBP: '\u00a3',
};

export function getPrimaryPpdReferencePrice(
  album: AlbumPricingSource
): string | null {
  const primaryPpdPrice = album.albumSalesTeams?.find(
    (salesTeam) => salesTeam.isPrimary
  )?.price;

  if (primaryPpdPrice === null || primaryPpdPrice === undefined) {
    return null;
  }

  if (
    typeof primaryPpdPrice === 'string' &&
    primaryPpdPrice.trim().length === 0
  ) {
    return null;
  }

  const numericPrice = Number(primaryPpdPrice);
  return Number.isFinite(numericPrice) ? numericPrice.toFixed(2) : null;
}

export function getPrimaryPpdReferencePriceOrLegacyPriceCode(
  album: AlbumPricingSource
): string | null {
  const primaryPpd = album.albumSalesTeams?.find(
    (salesTeam) => salesTeam.isPrimary
  );

  const primaryPpdPrice = getPrimaryPpdReferencePrice(album);
  if (primaryPpdPrice !== null) {
    return formatCurrencyPrice(
      primaryPpdPrice,
      getPrimaryPpdCurrencyCode(primaryPpd)
    );
  }

  return album.albumMetaData?.pricing?.priceCode ?? null;
}

function getPrimaryPpdCurrencyCode(
  primaryPpd:
    | NonNullable<AlbumPricingSource['albumSalesTeams']>[number]
    | undefined
): string {
  const currencyName = primaryPpd?.currencyName?.trim();
  if (currencyName) {
    return currencyName.toUpperCase();
  }

  const salesTeamName = primaryPpd?.salesTeamName?.trim().toLowerCase();
  switch (salesTeamName) {
    case 'australia':
      return 'AUD';
    case 'united kingdom':
      return 'GBP';
    case 'belgium':
    case 'france':
    case 'germany':
    case 'germany / austria':
    case 'ireland':
    case 'international':
    case 'netherlands':
      return 'EUR';
    case 'united states':
    default:
      return 'USD';
  }
}

function formatCurrencyPrice(
  price: number | string,
  currencyCode: string
): string {
  const numericPrice = Number(price);
  const normalizedCurrencyCode = currencyCode.toUpperCase();
  const currencySymbol =
    currencySymbols[normalizedCurrencyCode] ?? normalizedCurrencyCode;

  return `${currencySymbol} ${numericPrice.toFixed(2)} (${normalizedCurrencyCode})`;
}
